import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useAtomValue } from 'jotai';
import { WS_EVENTS } from '@twinden/shared';
import { currentFamilyAtom, currentUserAtom, memberNameAtom } from '../store';
import { api } from '../lib/api';
import {
  askForKeysOnWake,
  askForKeysUntilShared,
  deviceNotices,
  memberJoins,
  keyStorageState,
  keysArrived,
  onDeviceNotices,
  onKeyStorageState,
  ownDeviceNotice,
  onKeysArrivedQuietly,
  openAll,
  rememberSenderNames,
  setSenderNameResolver,
  shareKeys,
  startE2EE,
  type KeyStorageState,
} from '../lib/e2ee/session';
import { setWebSocketTransform, useWebSocket } from './useWebSocket';

/** Fired on window when a chat's keys arrive, so open views can reload. */
export const KEYS_ARRIVED_EVENT = 'twinden:keys-arrived';

/** What `KEYS_ARRIVED_EVENT` carries. `all`: which keys isn't known (D262). */
export interface KeysArrivedDetail {
  scope: 'chat' | 'family';
  scopeId: string;
  conversationId?: string;
  familyId?: string;
  all?: boolean;
}

/** Whether this browser can keep the device key, for the banner (D262). */
export function useKeyStorageState(): KeyStorageState {
  return useSyncExternalStore(onKeyStorageState, keyStorageState);
}

/**
 * When this device noticed each person gain a device (D303): userId → ms.
 * Local to this device; the chats show it as a quiet line.
 */
export function useDeviceNotices(): Readonly<Record<string, number>> {
  return useSyncExternalStore(onDeviceNotices, deviceNotices);
}

/** Who joined each chat or family after this device first looked (D343). */
export function useMemberJoins(): ReturnType<typeof memberJoins> {
  return useSyncExternalStore(onDeviceNotices, memberJoins);
}

/**
 * A new device on my own account not yet shown (M73): when this device
 * noticed it, or null.
 */
export function useOwnDeviceNotice(): number | null {
  return useSyncExternalStore(onDeviceNotices, ownDeviceNotice);
}

// End-to-end encryption for the signed-in user (M28): sets up this device's
// key, opens encrypted messages wherever they enter the app, and shares chat
// keys with family devices that need them. Mounted once, in the app shell.
export function useE2EE() {
  const userId = useAtomValue(currentUserAtom)?.id ?? null;
  const { onEvent, connectionEpoch } = useWebSocket();

  // Sealed items are named from their bound sender, through the family's
  // members and what I call them, never the server's strings (D327).
  const family = useAtomValue(currentFamilyAtom);
  const nameOf = useAtomValue(memberNameAtom);
  useEffect(() => {
    setSenderNameResolver(nameOf);
    // Kept (sealed) for people who leave and for opens before this loads.
    rememberSenderNames(
      new Map(
        (family?.members ?? []).map((member) => [
          member.userId,
          nameOf(member.userId) ?? member.user.displayName,
        ])
      )
    );
  }, [family, nameOf]);

  useEffect(() => {
    api.setResponseTransform(openAll);
    setWebSocketTransform(openAll);
    return () => {
      api.setResponseTransform(null);
      setWebSocketTransform(null);
    };
  }, []);

  // On load, and whenever the tab comes back: ask over HTTP for keys this
  // device lacks, whether or not the socket ever connects (D262).
  useEffect(() => {
    void startE2EE(userId).then(() => {
      if (!userId) return;
      void shareKeys();
      void askForKeysOnWake();
    });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void askForKeysOnWake();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [userId]);

  // An ask learned the keys came while no `keys:shared` reached this page.
  useEffect(
    () =>
      onKeysArrivedQuietly(() => {
        const detail: KeysArrivedDetail = {
          scope: 'chat',
          scopeId: '*',
          all: true,
        };
        window.dispatchEvent(new CustomEvent(KEYS_ARRIVED_EVENT, { detail }));
      }),
    []
  );

  // On every connect (D57): there may be new devices to give keys to, and
  // this device may still lack keys itself — registration asked only once,
  // and a reload doesn't register again, so it asks again here (and again
  // later while nothing arrives); whichever family device is online with
  // the keys answers.
  useEffect(() => {
    if (!userId || connectionEpoch === 0) return;
    void shareKeys();
    void askForKeysUntilShared();
  }, [userId, connectionEpoch]);

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event === WS_EVENTS.KEYS_NEEDED) void shareKeys();
        if (event === WS_EVENTS.KEYS_SHARED) {
          const detail = payload as KeysArrivedDetail;
          keysArrived(detail.scope, detail.scopeId);
          window.dispatchEvent(new CustomEvent(KEYS_ARRIVED_EVENT, { detail }));
        }
      }),
    [onEvent]
  );
}

/** Runs `onArrived` when this chat's or family's keys reach this device. */
export function useOnKeysArrived(
  scope: 'chat' | 'family',
  id: string | null | undefined,
  onArrived: () => void
) {
  const callback = useRef(onArrived);
  callback.current = onArrived;
  useEffect(() => {
    if (!id) return;
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<KeysArrivedDetail>).detail;
      if (detail.all || (detail.scope === scope && detail.scopeId === id))
        callback.current();
    };
    window.addEventListener(KEYS_ARRIVED_EVENT, listener);
    return () => window.removeEventListener(KEYS_ARRIVED_EVENT, listener);
  }, [scope, id]);
}
